// Ruta y filtro de planta del portal (PP-62): logica pura, sin React ni
// window, con test al lado. El shell (src/shell) lee window.location con
// estas funciones y empuja historia con history.pushState, igual que la torre
// (apps/tower/src/logic/navigation.ts): para cuatro secciones no hace falta
// una libreria de rutas.
//
// La URL guarda la seccion (pathname) y la planta filtrada (`?site=`), asi
// recargar no saca al operador de donde estaba. La planta de la URL es una
// preferencia de vista, nunca un tenant: la API valida cada request contra la
// habilitacion activa (ADR-015). Por eso una planta que la cuenta no tiene se
// ignora y el filtro cae a "Todas" sin error.
import type { SiteAccess } from "../sites/port.js";
import { cantidadDePlantas } from "./sites.js";

const SITE_PARAM = "site";
const VIEW_PARAM = "vista";

/** Lo que la URL dice: seccion y planta filtrada (`null` es "Todas"). */
export interface PortalRoute {
  readonly path: string;
  readonly siteId: string | null;
}

/** Una planta para el filtro: una por `siteId`, aunque tenga dos habilitaciones. */
export interface SiteOption {
  readonly siteId: string;
  readonly name: string;
}

/** Barras finales y mayusculas se toleran; una ruta desconocida es la primera seccion. */
export function pathFromPathname(pathname: string, paths: readonly string[]): string {
  const clean = pathname.trim().replace(/\/+$/, "").toLowerCase();
  const match = paths.find((path) => path.replace(/\/+$/, "").toLowerCase() === clean);
  return match ?? paths[0] ?? "/";
}

export function routeFromLocation(pathname: string, search: string, paths: readonly string[]): PortalRoute {
  const siteId = new URLSearchParams(search).get(SITE_PARAM);
  return { path: pathFromPathname(pathname, paths), siteId: siteId === null || siteId === "" ? null : siteId };
}

export function hrefFor(route: PortalRoute, view: string | null = null): string {
  const params = new URLSearchParams();
  if (route.siteId !== null) params.set(SITE_PARAM, route.siteId);
  if (view !== null && view !== "") params.set(VIEW_PARAM, view);
  const query = params.toString();
  return query === "" ? route.path : `${route.path}?${query}`;
}

/**
 * Vista secundaria dentro de una seccion (`?vista=carga` en Viajes), aparte
 * de la ruta y la planta: una seccion que no la usa la ignora, y cambiar de
 * seccion la deja atras. `null` es la vista principal.
 */
export function viewFromLocation(search: string): string | null {
  const view = new URLSearchParams(search).get(VIEW_PARAM);
  return view === null || view === "" ? null : view;
}

/** Plantas distintas, en el orden en que llegan (logic/sites.ts ya las ordena). */
export function siteOptions(plantas: readonly SiteAccess[]): readonly SiteOption[] {
  const seen = new Set<string>();
  const options: SiteOption[] = [];
  for (const planta of plantas) {
    if (seen.has(planta.siteId)) continue;
    seen.add(planta.siteId);
    options.push({ siteId: planta.siteId, name: planta.name });
  }
  return options;
}

/** Con una sola planta no hay nada que filtrar: el filtro no se muestra. */
export function showSiteFilter(plantas: readonly SiteAccess[]): boolean {
  return cantidadDePlantas(plantas) > 1;
}

/**
 * La planta que la vista usa de verdad. Una planta de la URL que la cuenta no
 * tiene (otra cuenta, habilitacion revocada, URL editada a mano) da "Todas".
 * Con una sola planta, esa es la planta: no se le pide al operador que la
 * elija de un filtro que no ve.
 */
export function effectiveSiteId(plantas: readonly SiteAccess[], requested: string | null): string | null {
  const options = siteOptions(plantas);
  if (options.length === 1) return options[0]?.siteId ?? null;
  if (requested === null) return null;
  return options.some((option) => option.siteId === requested) ? requested : null;
}

/**
 * Las habilitaciones de la planta filtrada. Pueden ser mas de una: la misma
 * empresa puede estar habilitada en una planta con dos ids locales de
 * proveedor, y cada una tiene su bandeja y su nomina. Con "Todas" no hay
 * ninguna hasta que la API lea varias plantas juntas (PP-63).
 */
export function accessesForSite(plantas: readonly SiteAccess[], siteId: string | null): readonly SiteAccess[] {
  if (siteId === null) return [];
  return plantas.filter((planta) => planta.siteId === siteId);
}

// --- Barra lateral (rediseño del portal, docs/PLAN-REDISENO-PORTAL.md §6.2) ---

/**
 * Preferencia del operador para la barra lateral: con icono y texto
 * ("ancha") o solo iconos ("compacta"). `null` es "sin preferencia": ancha en
 * escritorio y compacta en tablet, decidido por el ancho de la pantalla.
 */
export type NavPreference = "ancha" | "compacta";

/** Con cinco secciones o mas la navegacion es barra lateral; con menos, tabs. */
export const SIDEBAR_MIN_SECTIONS = 5;

export function usesSidebar(sectionCount: number): boolean {
  return sectionCount >= SIDEBAR_MIN_SECTIONS;
}

/** Lo guardado en el navegador es un valor de dos; cualquier otra cosa es "sin preferencia". */
export function parseNavPreference(raw: string | null | undefined): NavPreference | null {
  return raw === "ancha" || raw === "compacta" ? raw : null;
}

/**
 * La barra se ve ancha con la preferencia "ancha" o, sin preferencia, en una
 * pantalla ancha. `wideViewport` lo decide quien conoce la pantalla.
 */
export function navIsWide(preference: NavPreference | null, wideViewport: boolean): boolean {
  return preference === null ? wideViewport : preference === "ancha";
}

/** Plegar o desplegar: la preferencia nueva es lo contrario de lo que se ve. */
export function toggledNav(preference: NavPreference | null, wideViewport: boolean): NavPreference {
  return navIsWide(preference, wideViewport) ? "compacta" : "ancha";
}
